import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import ClientFields, { EMPTY_CLIENT, clientError } from './ClientFields';

const EMPTY = EMPTY_CLIENT;

// Dodavanje novog kupca (/klijenti/novi) ili izmena postojećeg (/klijenti/:id/izmeni)
export default function ClientForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { activeAgencyId } = useAgency();
  const { t, tr } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteStep, setDeleteStep] = useState(0); // 0 = ništa, 1 = prva provera, 2 = poslednja provera
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const { data, error: err } = await supabase.from('customers').select('*').eq('id', id).maybeSingle();
      if (err) setError(err.message);
      if (data) {
        const next = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => { if (data[k] !== null && data[k] !== undefined) next[k] = data[k]; });
        setForm(next);
      }
      setLoading(false);
    })();
  }, [id, isEdit]);

  const [touched, setTouched] = useState({ pib: false, mb: false });

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    const vErr = clientError(form, tr);
    if (vErr) { setTouched({ pib: true, mb: true }); setError(vErr); return; }
    setSaving(true);
    const payload = { ...form, name: form.name.trim() };
    const write = (pl) => (isEdit
      ? supabase.from('customers').update(pl).eq('id', id)
      : supabase.from('customers').insert({ ...pl, agency_id: activeAgencyId }));
    let { error: err } = await write(payload);
    // Ako u bazi još nema kolone "is_individual", sačuvaj bez nje
    if (err && /is_individual/.test(err.message || '')) {
      const { is_individual, ...rest } = payload; // eslint-disable-line no-unused-vars
      ({ error: err } = await write(rest));
    }
    setSaving(false);
    if (err) {
      setError(err.message + (err.details ? ' — ' + err.details : ''));
      return;
    }
    navigate('/klijenti');
  };

  const handleDelete = async () => {
    setError('');
    setDeleting(true);
    // Zaštita: kupac vezan za izdate dokumente se ne briše
    const { count, error: countErr } = await supabase
      .from('invoices')
      .select('id', { count: 'exact', head: true })
      .eq('customer_id', id);
    if (countErr) {
      setDeleting(false);
      setError(countErr.message);
      return;
    }
    if (count > 0) {
      setDeleting(false);
      setDeleteStep(0);
      setError(t('customerHasInvoices', count));
      return;
    }
    const { error: delErr } = await supabase.from('customers').delete().eq('id', id);
    setDeleting(false);
    if (delErr) {
      setError(delErr.message + (delErr.details ? ' — ' + delErr.details : ''));
      return;
    }
    navigate('/klijenti');
  };

  if (loading) return <div style={styles.empty}>{t('loadingGeneric')}</div>;

  return (
    <div style={styles.page}>
      <button onClick={() => navigate('/klijenti')} style={styles.backBtn}>{t('backToClients')}</button>
      <h1 style={styles.heading}>{isEdit ? t('editClient') : t('newClientHeading')}</h1>

      <form onSubmit={handleSave} style={styles.card}>
        <ClientFields form={form} setForm={setForm} touched={touched} setTouched={setTouched} />

        {error && <div style={styles.errorBox}>{error}</div>}

        <button type="submit" disabled={saving} style={{ ...styles.saveBtn, opacity: saving ? 0.6 : 1 }}>
          {saving ? t('savingEllipsisShort') : (isEdit ? t('saveChanges') : t('saveBtnShort'))}
        </button>
      </form>

      {isEdit && (
        <div style={styles.deleteArea}>
          {deleteStep === 0 && (
            <button type="button" onClick={() => { setError(''); setDeleteStep(1); }} style={styles.deleteLink}>
              <Icon name="trash" size={16} />
              {t('deleteCustomerBtn')}
            </button>
          )}
          {deleteStep === 1 && (
            <div style={styles.confirmBox}>
              <div style={styles.confirmText}>{t('deleteCustomerConfirm1', form.name)}</div>
              <div style={styles.confirmRow}>
                <button type="button" onClick={() => setDeleteStep(0)} style={styles.cancelBtn}>{t('cancelBtn')}</button>
                <button type="button" onClick={() => setDeleteStep(2)} style={styles.continueBtn}>{t('deleteContinueBtn')}</button>
              </div>
            </div>
          )}
          {deleteStep === 2 && (
            <div style={{ ...styles.confirmBox, borderColor: 'var(--line)', background: 'var(--red-100)' }}>
              <div style={{ ...styles.confirmText, color: 'var(--red-600)', fontWeight: 700 }}>{t('deleteCustomerConfirm2')}</div>
              <div style={styles.confirmRow}>
                <button type="button" onClick={() => setDeleteStep(0)} disabled={deleting} style={styles.cancelBtn}>{t('cancelBtn')}</button>
                <button type="button" onClick={handleDelete} disabled={deleting} style={styles.deleteFinalBtn}>
                  {deleting ? t('deletingEllipsis') : t('deleteFinalBtn')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 560, margin: '0 auto' },
  empty: { textAlign: 'center', color: 'var(--ink-500)', fontSize: 14, padding: '40px 0' },
  backBtn: { border: 'none', background: 'none', color: 'var(--teal-700)', fontWeight: 600, fontSize: 13, marginBottom: 8, padding: 0 },
  heading: { fontSize: 22, marginBottom: 16 },
  card: { background: 'var(--surface)', borderRadius: 10, padding: 20, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--line)', display: 'flex', flexDirection: 'column' },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5, display: 'block' },
  input: { border: '1.5px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 14, width: '100%', marginBottom: 12 },
  inputBad: { borderColor: 'var(--red-600)', background: 'var(--red-100)' },
  fieldError: { fontSize: 12, color: 'var(--red-600)', marginTop: -8, marginBottom: 12, lineHeight: 1.4 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-700)', margin: '2px 0 14px' },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12, wordBreak: 'break-word' },
  deleteArea: { marginTop: 18 },
  deleteLink: { display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', color: 'var(--red-600)', fontWeight: 600, fontSize: 13, padding: 0, cursor: 'pointer' },
  confirmBox: { border: '1.5px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: 16 },
  confirmText: { fontSize: 14, color: 'var(--ink-900)', marginBottom: 12, lineHeight: 1.5 },
  confirmRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' },
  cancelBtn: { border: '1.5px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '9px 16px', fontWeight: 600, fontSize: 13, color: 'var(--ink-700)' },
  continueBtn: { border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 10, padding: '9px 16px', fontWeight: 700, fontSize: 13 },
  deleteFinalBtn: { border: 'none', background: 'var(--red-600)', color: '#fff', borderRadius: 10, padding: '9px 16px', fontWeight: 700, fontSize: 13 },
  saveBtn: { width: '100%', minHeight: 34, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13, marginTop: 4 },
};
